import React from 'react';
import { Container, Card, Button, Table, Spinner, Alert, Form, Row, Col, Modal } from 'react-bootstrap';
import {
  PencilSquareIcon,
  ArrowLeftIcon,
  TrashIcon,
  PlusIcon,
  CheckIcon,
  CalendarIcon,
} from '@heroicons/react/24/solid';
import Header from '../common/Header';
import Footer from '../common/Footer';
import { useEditOrder } from './hooks/useEditOrder';

const EditOrder: React.FC = () => {
  const {
    navigate,
    isExpired,
    id,
    order,
    loading,
    items,
    deliveryDate,
    notes,
    saving,
    error,
    setError,
    success,
    showAddModal,
    setShowAddModal,
    selectedProduct,
    setSelectedProduct,
    addQuantity,
    setAddQuantity,
    minDeliveryDate,
    leadDays,
    updateItemQuantity,
    removeItem,
    handleAddProduct,
    handleSave,
    setFormData,
    availableProducts,
  } = useEditOrder();

  if (loading) {
    return (
      <div className="d-flex flex-column min-vh-100 app-background">
        <Header />
        <div className="flex-grow-1 d-flex justify-content-center align-items-center">
          <Spinner animation="border" variant="primary" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!order || order.status !== 'pending') {
    return (
      <div className="d-flex flex-column min-vh-100 app-background">
        <Header />
        <div className="flex-grow-1">
          <Container className="py-5 text-center">
            <h3 className="text-danger mb-3">Cannot Edit</h3>
            <p className="text-muted">
              {!order ? 'Order not found.' : 'Only pending orders can be edited.'}
            </p>
            <Button variant="primary" onClick={() => navigate('/orders')}>
              <ArrowLeftIcon style={{ width: '16px', height: '16px' }} className="me-2" />
              Back to Orders
            </Button>
          </Container>
        </div>
        <Footer />
      </div>
    );
  }

  // Products not yet in order (for add modal)
  // availableProducts is provided by useEditOrder hook

  return (
    <div className="d-flex flex-column min-vh-100 app-background">
      <Header />
      <div className="flex-grow-1">
        <Container className="py-3 py-md-5 page-content-padding">
          <Button variant="outline-secondary" className="mb-4" onClick={() => navigate(`/orders/${id}`)}>
            <ArrowLeftIcon style={{ width: '16px', height: '16px' }} className="me-2" />
            Back to Order
          </Button>

          <div className="text-center mb-3 mb-md-4">
            <h1 className="display-5 fw-bold text-primary mb-2 d-flex align-items-center justify-content-center gap-2">
              <PencilSquareIcon style={{ width: '36px', height: '36px' }} />
              Edit Order {order.order_number}
            </h1>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          <Row className="g-4">
            <Col lg={8}>
              <Card className="professional-card mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Order Items</h5>
                  <Button variant="outline-primary" size="sm" onClick={() => setShowAddModal(true)} disabled={isExpired}>
                    <PlusIcon style={{ width: '16px', height: '16px' }} className="me-1" />
                    Add Item
                  </Button>
                </Card.Header>
                <Card.Body>
                  {/* Desktop Table */}
                  <div className="d-none d-md-block">
                    <Table hover responsive>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th className="text-center" style={{ width: '200px' }}>Quantity</th>
                          <th style={{ width: '80px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <tr key={item.product_id}>
                            <td className="align-middle">
                              <strong>{item.product_name}</strong>
                              <small className="text-muted d-block">per {item.unit}</small>
                            </td>
                            <td className="align-middle">
                              <div className="d-flex align-items-center justify-content-center">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="rounded-circle"
                                  style={{ width: '32px', height: '32px' }}
                                  onClick={() => updateItemQuantity(index, item.quantity - 1)}
                                  disabled={item.quantity <= 1}
                                >−</Button>
                                <span className="mx-3 fw-bold fs-5">{item.quantity}</span>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="rounded-circle"
                                  style={{ width: '32px', height: '32px' }}
                                  onClick={() => updateItemQuantity(index, item.quantity + 1)}
                                >+</Button>
                              </div>
                            </td>
                            <td className="align-middle">
                              <Button variant="outline-danger" size="sm" onClick={() => removeItem(index)}>
                                <TrashIcon style={{ width: '16px', height: '16px' }} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {/* Mobile Card Layout */}
                  <div className="d-md-none">
                    {items.map((item, index) => (
                      <Card key={item.product_id} className="mb-2 border" style={{ borderRadius: '10px' }}>
                        <Card.Body className="p-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <strong>{item.product_name}</strong>
                              <small className="text-muted d-block">per {item.unit}</small>
                            </div>
                            <Button variant="outline-danger" size="sm" onClick={() => removeItem(index)}>
                              <TrashIcon style={{ width: '14px', height: '14px' }} />
                            </Button>
                          </div>
                          <div className="d-flex align-items-center">
                            <Button variant="outline-primary" size="sm"
                              onClick={() => updateItemQuantity(index, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                              style={{ minWidth: '32px' }}
                            >−</Button>
                            <span className="mx-3 fw-bold">{item.quantity}</span>
                            <Button variant="outline-primary" size="sm"
                              onClick={() => updateItemQuantity(index, item.quantity + 1)}
                              style={{ minWidth: '32px' }}
                            >+</Button>
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <div className="sticky-top" style={{ top: '2rem' }}>
                <Card className="professional-card mb-3">
                  <Card.Header>
                    <h6 className="mb-0 d-flex align-items-center gap-2">
                      <CalendarIcon style={{ width: '18px', height: '18px' }} />
                      Delivery Date
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Form.Control
                      type="date"
                      value={deliveryDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, deliveryDate: e.target.value }))}
                      min={minDeliveryDate}
                      className="modern-input"
                    />
                    {leadDays > 0 && (
                      <small className="text-muted mt-1 d-block">
                        Minimum {leadDays} day{leadDays > 1 ? 's' : ''} advance notice required.
                      </small>
                    )}
                  </Card.Body>
                </Card>

                <Card className="professional-card mb-3">
                  <Card.Header>
                    <h6 className="mb-0">Order Notes</h6>
                  </Card.Header>
                  <Card.Body>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Special instructions..."
                      className="modern-input"
                    />
                  </Card.Body>
                </Card>

                <div className="d-grid gap-2">
                  <Button
                    variant="success"
                    size="lg"
                    onClick={handleSave}
                    disabled={saving || items.length === 0 || isExpired}
                  >
                    {saving ? (
                      <><Spinner size="sm" className="me-2" />Saving...</>
                    ) : (
                      <><CheckIcon style={{ width: '20px', height: '20px' }} className="me-2" />Save Changes</>
                    )}
                  </Button>
                  <Button variant="outline-secondary" onClick={() => navigate(`/orders/${id}`)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <Footer />

      {/* Add Product Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Product</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Product</Form.Label>
            <Form.Select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value ? parseInt(e.target.value) : '')}
            >
              <option value="">Select a product...</option>
              {availableProducts.map(p => (
                <option key={p.id} value={p.id}>{p.name} — ${typeof p.price_per_unit === 'string' ? parseFloat(p.price_per_unit).toFixed(2) : p.price_per_unit.toFixed(2)}/{p.unit}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Quantity</Form.Label>
            <Form.Control
              type="number"
              min={1}
              value={addQuantity}
              onChange={(e) => setAddQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleAddProduct} disabled={!selectedProduct}>
            <PlusIcon style={{ width: '16px', height: '16px' }} className="me-1" />
            Add
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EditOrder;
